export interface StoredAssistantConversation {
  sessionId: number;
  clientSessionId?: string;
  expiresAt: string;
  lastActivityAt: string;
}

const KEY_PREFIX = "indexaai:assistant:history:";
const MAX_HISTORY = 8;

function storageKey(publicKey: string, visitorId: string): string {
  return `${KEY_PREFIX}${publicKey}:${visitorId}`;
}

function isValidEntry(value: unknown, now: number): value is StoredAssistantConversation {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Partial<StoredAssistantConversation>;
  return (
    typeof entry.sessionId === "number" &&
    Number.isSafeInteger(entry.sessionId) &&
    entry.sessionId > 0 &&
    (entry.clientSessionId === undefined ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        entry.clientSessionId,
      )) &&
    typeof entry.expiresAt === "string" &&
    Date.parse(entry.expiresAt) > now &&
    typeof entry.lastActivityAt === "string" &&
    Number.isFinite(Date.parse(entry.lastActivityAt))
  );
}

function writeHistory(key: string, entries: StoredAssistantConversation[]): void {
  try {
    if (entries.length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(entries));
  } catch {
    // Sem armazenamento persistente, o chat continua disponível na aba atual.
  }
}

/** Só IDs e datas ficam no navegador; mensagens e fotos continuam no servidor. */
export function readStoredAssistantHistory(
  publicKey: string,
  visitorId: string,
  now = Date.now(),
): StoredAssistantConversation[] {
  const key = storageKey(publicKey, visitorId);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    const entries = Array.isArray(parsed)
      ? parsed.filter((entry): entry is StoredAssistantConversation => isValidEntry(entry, now))
      : [];
    const sorted = entries
      .sort((left, right) => Date.parse(right.lastActivityAt) - Date.parse(left.lastActivityAt))
      .slice(0, MAX_HISTORY);
    if (sorted.length !== entries.length) writeHistory(key, sorted);
    return sorted;
  } catch {
    return [];
  }
}

export function rememberAssistantConversation(
  publicKey: string,
  visitorId: string,
  conversation: StoredAssistantConversation,
): StoredAssistantConversation[] {
  const key = storageKey(publicKey, visitorId);
  const entries = readStoredAssistantHistory(publicKey, visitorId).filter(
    (entry) => entry.sessionId !== conversation.sessionId,
  );
  const next = [conversation, ...entries]
    .sort((left, right) => Date.parse(right.lastActivityAt) - Date.parse(left.lastActivityAt))
    .slice(0, MAX_HISTORY);
  writeHistory(key, next);
  return next;
}

export function forgetAssistantConversation(
  publicKey: string,
  visitorId: string,
  sessionId: number,
): StoredAssistantConversation[] {
  const key = storageKey(publicKey, visitorId);
  const next = readStoredAssistantHistory(publicKey, visitorId).filter(
    (entry) => entry.sessionId !== sessionId,
  );
  writeHistory(key, next);
  return next;
}
